import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import EpsfPageBreadcrumb from "./EpsfPageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import { getUserData } from "../../../services/apiService";
import { EpsfAccessGate, useEpsfAccess } from "./epsfAccess";
import { useEpsfStudySite } from "./epsfLedgerUi";
import { EPSF_EVIDENCE_PATH } from "./epsfNav";
import { fetchReadinessReport } from "../isf/isfPlaceholderService";
import "./EpsfPages.scss";

const SITE_FIELD_OPTIONS = [
  { key: "SiteName", label: "Site" },
  { key: "ReadinessPercent", label: "Readiness" },
  { key: "CompleteCount", label: "Complete" },
  { key: "MissingCount", label: "Missing" },
];

const SITE_DEFAULT_COLUMNS = [
  "SiteName",
  "ReadinessPercent",
  "CompleteCount",
  "MissingCount",
];

const RISK_FIELD_OPTIONS = [
  { key: "Item", label: "Item" },
  { key: "SiteName", label: "Site" },
  { key: "DueDate", label: "Due" },
  { key: "RiskBucket", label: "Bucket" },
];

const RISK_DEFAULT_COLUMNS = ["Item", "SiteName", "DueDate", "RiskBucket"];

export default function EpsfReports() {
  const { loading, canView, moduleAccess, canReports, canEvidence, userData } = useEpsfAccess();
  const allow = canView && moduleAccess && canReports;
  const ctx = useEpsfStudySite();
  const [error, setError] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [sites, setSites] = useState([]);
  const [atRisk, setAtRisk] = useState([]);

  const refresh = useCallback(async () => {
    setError("");
    if (!ctx.studyId) {
      setSummary(null);
      setSites([]);
      setAtRisk([]);
      setListLoading(false);
      return;
    }
    const user = userData || getUserData() || {};
    setListLoading(true);
    try {
      const report = await fetchReadinessReport({
        organizationId: user.OrganizationId || user.organizationId || "",
        studyId: ctx.studyId,
        templateCategory: "ePSF",
        createdBy: user.StaffId || user.staffId || "",
        siteIdRef: ctx.siteId || null,
      });
      setSummary(report.summary || null);
      setSites(report.siteComparison || []);
      setAtRisk(report.atRiskDetail || []);
    } catch (err) {
      setError(err?.message || "Could not load pharmacy readiness.");
    } finally {
      setListLoading(false);
    }
  }, [ctx.studyId, ctx.siteId, userData]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const pct = Number(
    summary?.ReadinessPercent ?? summary?.readinessPercent ?? summary?.ReadinessPct ?? 0,
  );

  const renderSiteCell = useCallback((row, field) => {
    if (field === "SiteName") {
      return row.SiteName || row.siteName || row.SiteCode || "—";
    }
    if (field === "ReadinessPercent") {
      const v = row.ReadinessPercent ?? row.readinessPercent;
      if (v == null || v === "") return "—";
      return `${v}%`;
    }
    if (field === "CompleteCount") {
      return row.CompleteCount ?? row.completeCount ?? "—";
    }
    if (field === "MissingCount") {
      return row.MissingCount ?? row.missingCount ?? "—";
    }
    const value = row[field];
    return value == null || value === "" ? "—" : String(value);
  }, []);

  const renderRiskCell = useCallback((row, field) => {
    if (field === "Item") {
      return row.PlaceholderName || row.Title || row.title || "—";
    }
    if (field === "SiteName") {
      return row.SiteName || row.siteName || "—";
    }
    if (field === "DueDate") {
      return row.DueDate ? new Date(row.DueDate).toLocaleDateString() : "—";
    }
    if (field === "RiskBucket") {
      return row.RiskBucket || row.riskBucket || "—";
    }
    const value = row[field];
    return value == null || value === "" ? "—" : String(value);
  }, []);

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      <div className="epsf-list-page">
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "Pharmacy readiness" },
          ]}
        />
        <div className="epsf-receive">
          <p className="epsf-hub__hint" style={{ marginTop: 0 }}>
            Pharmacy evidence readiness for category <strong>ePSF</strong> (reuses the binder
            engine; gated by ePSF licence).
            {canEvidence ? (
              <>
                {" "}
                <Link to={EPSF_EVIDENCE_PATH}>Open evidence file</Link>
                {" · "}
                <Link to="/isf/reports">Full ISF reports (select ePSF)</Link>
              </>
            ) : null}
          </p>
          {error ? <p className="epsf-receive__error">{error}</p> : null}

          <div className="epsf-custody" aria-label="Pharmacy readiness">
            <div className="epsf-custody__stat is-emphasis">
              <span className="epsf-custody__value">{Number.isFinite(pct) ? `${pct}%` : "—"}</span>
              <span className="epsf-custody__label">Readiness</span>
            </div>
            <div className="epsf-custody__stat">
              <span className="epsf-custody__value">
                {summary?.RequiredCount ?? summary?.requiredCount ?? "—"}
              </span>
              <span className="epsf-custody__label">Required</span>
            </div>
            <div className="epsf-custody__stat">
              <span className="epsf-custody__value">
                {summary?.CompleteCount ?? summary?.completeCount ?? "—"}
              </span>
              <span className="epsf-custody__label">Complete</span>
            </div>
            <div className="epsf-custody__stat">
              <span className="epsf-custody__value">
                {summary?.MissingCount ?? summary?.missingCount ?? "—"}
              </span>
              <span className="epsf-custody__label">Missing</span>
            </div>
            <div className="epsf-custody__stat">
              <span className="epsf-custody__value">{atRisk.length}</span>
              <span className="epsf-custody__label">At risk rows</span>
            </div>
          </div>

          <div className="epsf-report-wrap">
            <h3>Site comparison</h3>
            <ReportTable
              rows={sites}
              loading={listLoading}
              fieldOptions={SITE_FIELD_OPTIONS}
              defaultColumns={SITE_DEFAULT_COLUMNS}
              getFieldLabel={(field) =>
                SITE_FIELD_OPTIONS.find((x) => x.key === field)?.label || field
              }
              renderCell={renderSiteCell}
              getRowKey={(row, index) =>
                String(row.SiteId || row.siteId || index)
              }
              persistLayoutKey="epsf-reports-sites-v1"
              emptyMessage="No ePSF readiness rows yet. Set up an ePSF binder template for the study, then refresh."
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>

          <div className="epsf-report-wrap">
            <h3>Overdue & at risk</h3>
            <ReportTable
              rows={atRisk.slice(0, 50)}
              loading={listLoading}
              fieldOptions={RISK_FIELD_OPTIONS}
              defaultColumns={RISK_DEFAULT_COLUMNS}
              getFieldLabel={(field) =>
                RISK_FIELD_OPTIONS.find((x) => x.key === field)?.label || field
              }
              renderCell={renderRiskCell}
              getRowKey={(row, index) =>
                String(row.StudyPlaceholderId || index)
              }
              persistLayoutKey="epsf-reports-atrisk-v1"
              emptyMessage="No at-risk pharmacy placeholders."
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>
        </div>
      </div>
    </EpsfAccessGate>
  );
}
